/**
 * Broadcast detail — mostra shortlist gerado pelo match engine + actions accept/decline.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, MapPin, Radio, Star, X } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { DetailCard, DetailLayout } from "@/components/detail-layout";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { nerdsApi } from "@/lib/api-cadastros";
import { dispatchActionsApi } from "@/lib/api-job-extras";
import { dispatchApi, jobsApi } from "@/lib/api-jobs";
import { formatDateTime, formatRelative } from "@/lib/format";

export function BroadcastDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();

  const broadcast = useQuery({
    queryKey: ["broadcast", id],
    queryFn: async () => {
      const list = await dispatchApi.listBroadcasts({ size: 200 });
      return list.items.find((b) => b.id === id);
    },
    enabled: !!id,
  });
  const shortlist = useQuery({
    queryKey: ["broadcast-shortlist", id],
    queryFn: () => dispatchApi.getShortlist(id),
    enabled: !!id,
  });
  const job = useQuery({
    queryKey: ["broadcast-job", broadcast.data?.job_id],
    queryFn: () => jobsApi.get(broadcast.data!.job_id),
    enabled: !!broadcast.data?.job_id,
  });

  const accept = useMutation({
    mutationFn: (slId: string) => dispatchActionsApi.accept(slId),
    onSuccess: () => {
      toast.success("Chamado atribuído!");
      qc.invalidateQueries({ queryKey: ["broadcast", id] });
      qc.invalidateQueries({ queryKey: ["broadcast-shortlist", id] });
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const decline = useMutation({
    mutationFn: (slId: string) => dispatchActionsApi.decline(slId),
    onSuccess: () => {
      toast.success("Recusado");
      qc.invalidateQueries({ queryKey: ["broadcast-shortlist", id] });
    },
  });

  if (!broadcast.data) return <div className="text-muted-foreground py-10">Carregando…</div>;
  const b = broadcast.data;

  return (
    <DetailLayout
      backTo="/dispatch"
      eyebrow={`Broadcast #${b.batch_number}`}
      visual={
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-50 text-purple-600 dark:bg-purple-500/10">
          <Radio className="h-6 w-6" />
        </div>
      }
      title={job.data ? job.data.title : "—"}
      subtitle={
        job.data ? (
          <Link to={`/jobs/${job.data.id}`} className="text-primary-700 dark:text-primary-300 hover:underline font-mono text-xs">
            {job.data.code}
          </Link>
        ) : null
      }
      badges={
        <>
          <Badge variant="primary" dot>{b.status}</Badge>
          <Badge variant="default">{b.radius_km} km de raio</Badge>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <DetailCard title="Iniciado">
          <p className="text-sm font-medium">{formatDateTime(b.started_at)}</p>
          <p className="text-xs text-muted-foreground">{formatRelative(b.started_at)}</p>
        </DetailCard>
        <DetailCard title="Expira em">
          <p className="text-sm font-medium">{formatDateTime(b.expires_at)}</p>
          <p className="text-xs text-muted-foreground">{formatRelative(b.expires_at)}</p>
        </DetailCard>
        <DetailCard title="Candidatos">
          <p className="text-2xl font-bold tabular-nums">{shortlist.data?.total ?? 0}</p>
          <p className="text-xs text-muted-foreground">técnicos no shortlist</p>
        </DetailCard>
      </div>

      <DetailCard title="Shortlist" description="Técnicos ranqueados pelo match engine (geo + skill + rank + experiência)">
        {shortlist.isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : !shortlist.data || shortlist.data.items.length === 0 ? (
          <EmptyState
            icon={Radio}
            title="Sem candidatos"
            description="Nenhum técnico aprovado dentro do raio configurado."
          />
        ) : (
          <ul className="space-y-2 -m-6 p-3">
            {shortlist.data.items.map((sl) => (
              <ShortlistRow
                key={sl.id}
                sl={sl}
                onAccept={() => accept.mutate(sl.id)}
                onDecline={() => decline.mutate(sl.id)}
                acceptLoading={accept.isPending && accept.variables === sl.id}
                declineLoading={decline.isPending && decline.variables === sl.id}
              />
            ))}
          </ul>
        )}
      </DetailCard>
    </DetailLayout>
  );
}

interface ShortlistRowProps {
  sl: {
    id: string;
    nerd_id: string;
    match_score: number;
    distance_km: number;
    rank_position: number;
    accepted_at: string | null;
    declined_at: string | null;
  };
  onAccept: () => void;
  onDecline: () => void;
  acceptLoading: boolean;
  declineLoading: boolean;
}

function ShortlistRow({ sl, onAccept, onDecline, acceptLoading, declineLoading }: ShortlistRowProps) {
  const nerd = useQuery({
    queryKey: ["nerd-mini", sl.nerd_id],
    queryFn: () => nerdsApi.get(sl.nerd_id),
  });

  return (
    <li className="flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-slate-50/60 dark:hover:bg-white/[0.03]">
      <span className="text-xs font-bold tabular-nums text-muted-foreground w-5">#{sl.rank_position}</span>
      <Avatar size="md">
        <AvatarFallback name={nerd.data?.legal_name ?? "?"} />
      </Avatar>
      <Link to={`/nerds/${sl.nerd_id}`} className="min-w-0 flex-1">
        <p className="font-medium text-sm truncate hover:underline">
          {nerd.data?.legal_name ?? "Carregando…"}
        </p>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> {nerd.data?.rank_score ?? "—"}
          </span>
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {Number(sl.distance_km).toFixed(1)} km
          </span>
          <span>{nerd.data?.completed_jobs ?? 0} jobs</span>
        </div>
      </Link>
      <div className="text-right shrink-0 mr-2">
        <p className="text-lg font-bold tabular-nums text-primary-700 dark:text-primary-300">
          {Number(sl.match_score).toFixed(0)}
        </p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">score</p>
      </div>
      {sl.accepted_at ? (
        <Badge variant="success" dot>Aceitou</Badge>
      ) : sl.declined_at ? (
        <Badge variant="muted">Recusou</Badge>
      ) : (
        <div className="flex gap-1 shrink-0">
          <Button
            size="icon-sm"
            variant="outline"
            onClick={onDecline}
            disabled={declineLoading}
            className="text-rose-600"
            title="Recusar"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            onClick={onAccept}
            disabled={acceptLoading}
          >
            <Check className="h-3.5 w-3.5" /> Atribuir
          </Button>
        </div>
      )}
    </li>
  );
}
